/* Panels built for rebranded project creation, replacing shared/reg/panels/.
 * Currently still depends on a redux store with a user reducer.
 */

import React from 'react'
import {trackEventSP} from 'js/studios/shared/tracking'
import {LinkFormattedText} from 'js/studios/shared/utils'
import ButtonLoader from 'js/studios/shared/button_loader'
import {Frame, FrameHeader} from 'js/studios/shared/flows'
import {FlowFramesCtx} from 'js/studios/shared/unredux/contexts/flow'
import {loginFacebook, sendRequest, GoogleLoginButton, googleLoginAvailable, AppleSigninButton, appleSigninAvailable} from '../auth'
import FacebookSvg from "images/brand2025/social/facebook.svg"

export function FBButton({loading, onClick, signup}) {
  return <ButtonLoader className="btn btn--fb" loading={loading} onClick={onClick}>
    <FacebookSvg />
    {signup ? "Sign up" : "Log in"} with Facebook
  </ButtonLoader>
}

/* props:
 * - switchToSignUp: optional. if present, shows a prompt to switch to sign up.
 * - analyticsSource: optional. if present, used for some analtyics events
 * - switchToResetPassword: required callback
 * - onSuccess: required callback call after successful sign in.
 * - onFail: optional callback call after failed sign in attempt.
 * - children: additional content at bottom of frame
 */
class SignInForm extends React.Component {
  static contextType = FlowFramesCtx
  state = {
    email: "",
    password: "",
    loading: false,
    fbLoading: false,
    error: "",
  }

  isValid() {
    return this.state.email.length > 0 && this.state.password.length > 0
  }

  componentDidMount() {
    trackEventSP('Sign up or log in modal displayed', 'Customer sign up', {
      'source': this.props.analyticsSource, 'panel': 'login',
    })
  }

  signIn = ev => {
    ev.preventDefault()
    if (!this.state.fbLoading && !this.state.loading && this.isValid()){
      this.setState({loading: true})
      const data = {email: this.state.email, password: this.state.password, remember: false}
      sendRequest('login', data)
        .then(resp => {
          // we presume success case navigates in onSuccess so no `then` here
          this.props.onSuccess({method: "login", resp}).catch(err => {
            this.setState({loading: false, error: err})
          })
        })
        .catch(errorMessage => {
          this.props.onFail?.({method: "login", email: data.email, error: errorMessage})
          this.setState({loading: false, error: errorMessage})
        })
    }
  }

  signInFacebook = ev => {
    ev.preventDefault()
    if (this.state.loading || this.state.fbLoading) {
      return false
    }
    this.setState({fbLoading: true})

    loginFacebook()
      .then(resp => {
        // we presume success case navigates in onSuccess so no `then` here
        return this.props.onSuccess({method: "facebook", resp}).catch(err => this.setState({error: err}))
      })
      .catch(errorMessage => {
        this.props.onFail?.({method: "facebook", email: "", error: errorMessage})
        this.setState({fbLoading: false, error: errorMessage})
      })
  }

  signInGoogle = result => {
    if (result.error) {
      this.props.onFail?.({method: "google", email: "", error: result.error})
      this.setState({error: result.error})
    } else {
      // we presume success case navigates in onSuccess so no `then` here
      return this.props.onSuccess({method: "google", resp: result}).catch(err => this.setState({error: err}))
    }
  }

  signInApple = result => {
    if (result.error) {
      this.props.onFail?.({method: "apple", email: "", error: result.error})
    } else {
      // we presume success case navigates in onSuccess so no `then` here
      return this.props.onSuccess({method: "apple", resp: result}).catch(err => this.setState({error: err}))
    }
  }

  updateField(field, ev) {
    this.setState({[field]: ev.target.value})
  }

  render() {
    return <Frame cta={false} canSubmit={true} onSubmit={this.signIn}>
      {this.context
        ? <>
          <FrameHeader>Welcome back!</FrameHeader>
          <p className="txt--center mb--lg">Let’s get started! You are now ready to start your design experience</p>
        </>
        : null}
      <h3>Log in to your account</h3>
      <fieldset className="field--md mb--1">
        <label className="field--md">
          <input type="email" aria-label="Email" placeholder="Email" onChange={this.updateField.bind(this, 'email')} value={this.state.email} />
        </label>
        <label className="field--md">
          <input type="password" aria-label="Password" placeholder="Password" onChange={this.updateField.bind(this, 'password')} value={this.state.password} />
        </label>
      </fieldset>
      <div className="btn-group btn-group--stack field--md mb--1">
        <ButtonLoader className="btn btn--lg" disabled={this.state.loading || this.state.fbLoading} type="submit" loading={this.state.loading}>Sign in</ButtonLoader>
      </div>
      {this.state.error && <p className="flow-error"><LinkFormattedText>{this.state.error}</LinkFormattedText></p>}
      <p className="txt--center txt--body-2 mb--0">
        <a href="#" onClick={this.props.switchToResetPassword}>Forgot your password?</a>
      </p>
      <p className="txt--overline txt--red mt--2 mb--2">or</p>
      <div className="btn-group btn-group--stack field--md">
        {googleLoginAvailable() && <GoogleLoginButton handler={this.signInGoogle} /> }
        {appleSigninAvailable() && <AppleSigninButton handler={this.signInApple} />}
        <FBButton loading={this.state.fbLoading} onClick={this.signInFacebook} />
      </div>
      {this.props.switchToSignUp
        ? <p className="txt--center txt--body-2 mt--1">
          Don't have an account yet? <a href="#" onClick={this.props.switchToSignUp}><b>Sign up</b></a>
        </p>
        : null}
      {this.props.children ?? null}
    </Frame>
  }
}

/* props:
 * switchToSignIn: required callback
 * - analyticsSource: optional. if present, used for some analtyics events
 * - onSuccess: required callback call after successful sign in.
 * - onFail: optional callback call after failed sign in attempt.
 */
class ForgotPassword extends React.Component {
  static contextType = FlowFramesCtx
  state = {
    email: "",
    emailSent: false,
    error: "",
    loading: false,
  }

  updateEmail = ev => {
    this.setState({email: ev.target.value})
  }

  isValid() {
    if (this.state.email.length === 0) {
      this.setState({error: "Please provide an email address"})
      return false
    }
    return true
  }

  onSubmit = ev => {
    ev.preventDefault()
    if (!this.state.loading && this.isValid()){
      this.setState({error: "", loading: true})
      sendRequest('resetPassword', {email: this.state.email})
        .then(() => {
          this.props.onSuccess({method: "resetPassword", resp: {email: this.state.email}})
          this.setState({emailSent: true, loading: false})
        })
        .catch(errorMessage => {
          this.props.onFail?.({method: "resetPassword", email: this.state.email, error: errorMessage})
          this.setState({loading: false, error: errorMessage})
        })
    }
  }

  onResend = ev => {
    ev.preventDefault()
    this.setState({emailSent: false})
  }

  componentDidMount() {
    trackEventSP('Sign up or log in modal displayed', 'Customer sign up', {
      'source': this.props.analyticsSource, 'panel': 'forgot-password',
    })
  }

  render() {
    return <div className="flow-frame anim--fade-in">
      <div className="flow-frame__body">
        {this.context
          ? <FrameHeader>Reset password</FrameHeader>
          : <h3>Forgot your password?</h3>}
        {!this.state.emailSent && <form className="flow-frame__body anim--fade-in" onSubmit={this.onSubmit}>
          <p className="txt--center mb--lg">Enter your email address below and we'll send you a link to reset your password.</p>
          <fieldset className="field--md mb--1">
            <label className="field--md">
              <input type="email" aria-label="Email" placeholder="Email" onChange={this.updateEmail} value={this.state.email} />
            </label>
          </fieldset>
          <div className="btn-group btn-group--stack field--md mb--1">
            <ButtonLoader type="submit" className="btn btn--lg" loading={this.state.loading}>Reset</ButtonLoader>
          </div>
          {this.state.error && <p className="flow-error">{this.state.error}</p>}
          <p className="txt--center txt--body-2">
            <a href="#" onClick={this.props.switchToSignIn}>Back to Login</a>
          </p>
        </form>}
        {this.state.emailSent && <>
          <p className="txt--center anim--fade-in">We've sent an email to {this.state.email}. After you've reset your password, please return and sign in. <a href="#" onClick={this.onResend}>Resend?</a></p>
          <div className="btn-group btn-group--stack field--md anim--fade-in">
            <a className="btn btn--lg" href="#" onClick={this.props.switchToSignIn}>Return to sign in</a>
          </div>
        </>}
      </div>
    </div>
  }
}

/* props:
 * - switchToSignUp: optional. if present, shows a prompt to switch to sign up.
 * - analyticsSource: optional. if present, used for some analtyics events
 * - onSuccess: required callback call after successful sign in. Must return a promise: components
 *   here presume this handler will navigate the page on success so do not handle resolved values,
 *   but do handle failures (`.catch`) to display errors/reset loading. Also, the reset password
 *   case entirely ignores the returned promise, so the return value is not required there.
 * - onFail: optional callback call after failed sign in attempt.
 * - children: additional content for bottom of frame when showing sign in (not forgot password)
 */
export class SignIn extends React.Component {
  state = {frame: 'signin'}

  switchToFrame(frame, ev) {
    ev.preventDefault()
    this.setState({frame: frame})
  }

  render() {
    if (this.state.frame === 'signin') {
      return <SignInForm switchToResetPassword={this.switchToFrame.bind(this, 'reset-password')} analyticsSource={this.props.analyticsSource} switchToSignUp={this.props.switchToSignUp} onSuccess={this.props.onSuccess} onFail={this.props.onFail}>
        {this.props.children}
      </SignInForm>
    }
    return <ForgotPassword switchToSignIn={this.switchToFrame.bind(this, 'signin')} analyticsSource={this.props.analyticsSource} onSuccess={this.props.onSuccess} onFail={this.props.onFail} />
  }
}
